import { useState, useRef, useEffect } from 'react';
import { X, Send, Search, ShoppingCart, Headphones, Sparkles, Bot, User, Loader2, ChevronDown, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AGENTS = [
  {
    id: 'catalog',
    name: 'Catalog',
    icon: Search,
    color: 'from-cyan-500 to-blue-500',
    description: 'Find products & recommendations'
  },
  {
    id: 'execution',
    name: 'Checkout',
    icon: ShoppingCart,
    color: 'from-green-500 to-emerald-500',
    description: 'Complete purchases & financing'
  },
  {
    id: 'support',
    name: 'Support',
    icon: Headphones,
    color: 'from-orange-500 to-red-500',
    description: 'Orders, payments & help'
  }
];

export const AIChat = ({ isOpen, onClose }) => {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeAgent, setActiveAgent] = useState('catalog');
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showAgentPicker, setShowAgentPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Initialize with welcome message when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const agent = AGENTS.find(a => a.id === activeAgent);
      setMessages([{
        role: 'assistant',
        content: `Jambo! 👋 I'm the ${agent.name} Agent. ${agent.description}. How can I help you today?`,
        agent: activeAgent,
        timestamp: new Date().toISOString()
      }]);
      // Set initial suggestions
      if (activeAgent === 'catalog') {
        setSuggestions(['Show me phones under KES 100,000', 'What properties are in Karen?', 'Compare investment options']);
      } else if (activeAgent === 'execution') {
        setSuggestions(['Help me checkout', 'What are the financing options?', 'How does M-Pesa work?']);
      } else {
        setSuggestions(['Track my order', 'I have a payment issue', 'How do I get a refund?']);
      }
    }
  }, [isOpen, activeAgent, messages.length]);

  const switchAgent = (agentId) => {
    setActiveAgent(agentId);
    setSessionId(null);
    const agent = AGENTS.find(a => a.id === agentId);
    setMessages([{
      role: 'assistant',
      content: `Switching to ${agent.name} Agent. ${agent.description}. How can I help you?`,
      agent: agentId,
      timestamp: new Date().toISOString()
    }]);
    setShowAgentPicker(false);
    
    // Update suggestions for new agent
    if (agentId === 'catalog') {
      setSuggestions(['Show me phones under KES 100,000', 'What properties are in Karen?', 'Compare investment options']);
    } else if (agentId === 'execution') {
      setSuggestions(['Help me checkout', 'What are the financing options?', 'How does M-Pesa work?']);
    } else {
      setSuggestions(['Track my order', 'I have a payment issue', 'How do I get a refund?']);
    }
  };

  const sendMessage = async (text = input) => {
    if (!text.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: text.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setSuggestions([]);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.post(
        `${API_URL}/api/ai/chat`,
        {
          query: text.trim(),
          agent_type: activeAgent,
          session_id: sessionId
        },
        { headers }
      );

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        agent: response.data.agent_type,
        hasContext: response.data.has_context,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      setSessionId(response.data.session_id);
      
      if (response.data.suggestions?.length > 0) {
        setSuggestions(response.data.suggestions);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        agent: activeAgent,
        error: true,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const currentAgent = AGENTS.find(a => a.id === activeAgent);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="ai-chat-modal">
      <div 
        className="w-full max-w-lg bg-gradient-to-b from-slate-900 to-slate-950 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: 'calc(100vh - 2rem)', height: '600px' }}
      >
        {/* Header */}
        <div className={`bg-gradient-to-r ${currentAgent.color} p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white">Jambo AI</h3>
                <button 
                  onClick={() => setShowAgentPicker(!showAgentPicker)}
                  className="flex items-center gap-1 text-white/80 text-sm hover:text-white transition-colors"
                  data-testid="agent-picker-btn"
                >
                  <currentAgent.icon className="w-3 h-3" />
                  {currentAgent.name} Agent
                  <ChevronDown className={`w-3 h-3 transition-transform ${showAgentPicker ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="text-white hover:bg-white/20"
              data-testid="close-chat-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Agent Picker Dropdown */}
          {showAgentPicker && (
            <div className="mt-3 bg-white/10 backdrop-blur rounded-lg p-2 space-y-1">
              {AGENTS.map(agent => (
                <button
                  key={agent.id}
                  onClick={() => switchAgent(agent.id)}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                    agent.id === activeAgent ? 'bg-white/20' : 'hover:bg-white/10'
                  }`}
                  data-testid={`switch-agent-${agent.id}`}
                >
                  <agent.icon className="w-4 h-4 text-white" />
                  <div className="text-left">
                    <div className="text-white font-medium text-sm">{agent.name}</div>
                    <div className="text-white/70 text-xs">{agent.description}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'user' 
                    ? 'bg-gradient-to-r from-cyan-500 to-green-500' 
                    : `bg-gradient-to-r ${AGENTS.find(a => a.id === (msg.agent || activeAgent))?.color || 'from-gray-500 to-gray-600'}`
                }`}>
                  {msg.role === 'user' ? (
                    <User className="w-4 h-4 text-white" />
                  ) : (
                    <Bot className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className={`rounded-2xl px-4 py-2 ${
                  msg.role === 'user' 
                    ? 'bg-gradient-to-r from-cyan-500 to-green-500 text-white' 
                    : msg.error 
                      ? 'bg-red-500/20 text-red-200 border border-red-500/30'
                      : 'bg-slate-800 text-slate-100'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.hasContext && (
                    <div className="mt-1 text-xs opacity-60 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Context-aware response
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="flex gap-2 max-w-[85%]">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-r ${currentAgent.color}`}>
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-slate-800 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                    <span className="text-sm text-slate-400">Thinking...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions */}
        {suggestions.length > 0 && !loading && (
          <div className="px-4 pb-2">
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => sendMessage(suggestion)}
                  className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded-full transition-colors border border-slate-700"
                  data-testid={`suggestion-${idx}`}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="flex-1 bg-slate-800 border border-slate-700 rounded-full px-4 py-2 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className={`rounded-full bg-gradient-to-r ${currentAgent.color} hover:opacity-90 disabled:opacity-50`}
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">
            Powered by GPT-5.2 • {user ? `Logged in as ${user.phone}` : 'Guest mode'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Floating AI Button Component
export const AIFloatingButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-cyan-500 via-green-500 to-orange-500 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group z-40 animate-pulse hover:animate-none"
      data-testid="ai-floating-btn"
    >
      <MessageSquare className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
        <Sparkles className="w-2.5 h-2.5 text-white" />
      </span>
    </button>
  );
};

export default AIChat;
